<script setup>
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { collection, getDocs, getDoc, doc } from "firebase/firestore";
import db from "../main.js";

const router = useRouter();
const route = useRoute();

// URLパラメータからeventIdを取得
const eventId = route.params.event_id;
console.log("Event ID from URL:", eventId);

// ユーザー情報（propsから取得）
const props = defineProps({
  user: Object,
});

// 注文IDのリスト
const orderIds = ref([]);

// 商品データ（実際のFirestoreデータから取得）
const products = ref([]);

// イベントタイトル
const eventTitle = ref("イベント情報を読み込み中...");

// ローディング状態
const isLoading = ref(true);
const loadingMessage = ref("データを読み込み中...");

// ユーザーのサブコレクションから特定のeventIdの注文IDを取得
const fetchOrderIds = async () => {
  if (!props.user?.uid || !eventId) {
    console.log("User UID or Event ID not available");
    isLoading.value = false;
    return;
  }

  try {
    // loadingMessage.value = "注文データを読み込み中...";
    console.log("Fetching orders from user subcollection for event_id:", eventId);

    // ユーザーのサブコレクションから全注文を取得
    const querySnapshot = await getDocs(
      collection(db, "users", props.user.uid, "Goods_productorders")
    );

    const foundOrderIds = [];

    // 各ドキュメント（{orderID}）を処理
    for (const docSnapshot of querySnapshot.docs) {
      const orderId = docSnapshot.id; // ドキュメントID = orderId
      console.log("Order ID:", orderId);

      // Goods_productorders/{orderId}ドキュメントからevent_idを取得
      try {
        const orderDocRef = doc(db, "Goods_productorders", orderId);
        const orderDocSnap = await getDoc(orderDocRef);

        if (orderDocSnap.exists()) {
          console.log("Document data:", orderDocSnap.data());
          const orderData = orderDocSnap.data();
          const orderEventId = orderData.event_id;

          console.log("Event ID:", orderEventId);

          // eventIdが一致する場合のみ追加
          if (orderEventId === eventId) {
            foundOrderIds.push(orderId);
            console.log("Found matching order ID:", orderId, "for event:", orderEventId);
          }
        } else {
          console.log("No such document!");
        }
      } catch (error) {
        console.error(`Order document取得に失敗しました (orderId: ${orderId}):`, error);
      }
    }

    orderIds.value = foundOrderIds;
    console.log("Total matching order IDs found:", foundOrderIds.length);

    // 注文IDが取得できたら、全商品データを取得（当選・未当選含む）
    await fetchAllProducts(foundOrderIds);

    // ローディング完了
    isLoading.value = false;
  } catch (error) {
    console.error("Error fetching order IDs:", error);
    isLoading.value = false;
  }
};

// 全商品データを取得（当選・未当選含む）
const fetchAllProducts = async (orderIdsList) => {
  try {
    // loadingMessage.value = "商品データを読み込み中...";
    console.log("Fetching all products for eventId:", eventId);

    // 1. Goods_products/{eventId}/products サブコレクションから全商品を取得
    console.log("Fetching products from subcollection for eventId:", eventId);

    // サブコレクションから商品データを取得
    const productsSubcollectionRef = collection(db, "Goods_products", eventId, "products");
    const productsSnapshot = await getDocs(productsSubcollectionRef);
    console.log("Products subcollection result:", productsSnapshot.docs.length, "documents found");

    let allEventProducts = [];

    if (productsSnapshot.docs.length > 0) {
      // 各商品ドキュメントを処理
      productsSnapshot.forEach((doc) => {
        const productData = doc.data();
        console.log(`Product ${doc.id}:`, productData);

        // 商品データを配列に追加
        allEventProducts.push({
          productid: doc.id, // ドキュメントID = productId
          product_image: productData.product_image || "/img/comment_card.png",
          product_name: productData.product_name || "商品名不明",
          product_rank: productData.product_rank || "b",
          product_rank_ja: productData.product_rank_ja || "B賞",
          product_material: productData.product_material || "",
          product_size: productData.product_size || "",
        });
      });
    } else {
      console.log("No products found in subcollection for eventId:", eventId);
    }

    console.log("Final allEventProducts:", allEventProducts);
    console.log("Event products count:", allEventProducts.length);

    // 2. 注文された商品の数量を集計
    const orderedProductMap = new Map();
    console.log("Order IDs to process:", orderIdsList);

    for (const orderId of orderIdsList) {
      try {
        const productsDocRef = doc(db, "Goods_productorders", orderId, "orderproducts", "products");
        const productsDocSnap = await getDoc(productsDocRef);

        if (productsDocSnap.exists()) {
          const productsData = productsDocSnap.data();
          console.log(`Order ${orderId} products data:`, productsData);

          if (productsData.products && Array.isArray(productsData.products)) {
            console.log(`Order ${orderId} has ${productsData.products.length} products`);
            productsData.products.forEach((productData) => {
              const productid = productData.productid;
              console.log(`Processing product ${productid} from order ${orderId}`);

              if (orderedProductMap.has(productid)) {
                orderedProductMap.set(productid, orderedProductMap.get(productid) + 1);
              } else {
                orderedProductMap.set(productid, 1);
              }
            });
          } else {
            console.log(`Order ${orderId} has no products array`);
          }
        } else {
          console.log(`Order ${orderId} products document not found`);
        }
      } catch (error) {
        console.error(`Order products取得に失敗しました (orderId: ${orderId}):`, error);
      }
    }

    // 3. 全商品を処理（当選・未当選含む）
    console.log("Ordered product map:", orderedProductMap);
    console.log("Processing all event products...");

    const allProducts = allEventProducts.map((productData, index) => {
      const productid = productData.productid;
      const orderedQuantity = orderedProductMap.get(productid) || 0;

      console.log(`Product ${productid}: ordered quantity = ${orderedQuantity}`);

      return {
        id: `event_${index}`,
        productid: productid,
        product_image: productData.product_image || "/img/comment_card.png",
        product_name: productData.product_name || "商品名不明",
        product_rank: productData.product_rank || "b",
        product_rank_ja: productData.product_rank_ja || "B賞",
        quantity: orderedQuantity, // 注文された数量（0の場合は未当選）
      };
    });

    console.log("Final products array:", allProducts);

    // 4. product_rankで並び替え（ssr > r > a > b > c、同じランク内ではproductid順）
    const baseRankOrder = { ssr: 1, r: 2, a: 3, b: 4, c: 5 };

    allProducts.sort((a, b) => {
      const rankA = baseRankOrder[a.product_rank] || 999;
      const rankB = baseRankOrder[b.product_rank] || 999;

      // まずランクで比較
      if (rankA !== rankB) {
        return rankA - rankB;
      }

      // 同じランクの場合はproductidで比較（数値として比較）
      const productIdA = parseInt(a.productid, 10) || 0;
      const productIdB = parseInt(b.productid, 10) || 0;
      return productIdA - productIdB;
    });

    products.value = allProducts;
    console.log("Total products fetched:", allProducts.length);
    console.log(
      "Products with quantities (sorted by rank):",
      allProducts.map((p) => ({
        name: p.product_name,
        quantity: p.quantity,
        rank: p.product_rank,
      }))
    );
  } catch (error) {
    console.error("Error fetching all products:", error);
  }
};

// イベントタイトルを取得
const fetchEventTitle = async () => {
  if (!eventId) {
    console.log("Event ID not available");
    return;
  }

  try {
    // loadingMessage.value = "イベント情報を読み込み中...";
    console.log("Fetching event title for eventId:", eventId);

    // Goods_products/{eventId}からイベント情報を取得
    const eventDocRef = doc(db, "Goods_products", eventId);
    const eventDocSnap = await getDoc(eventDocRef);

    if (eventDocSnap.exists()) {
      const eventData = eventDocSnap.data();
      const title =
        eventData.eventtitle || `${eventData.vendorname_ja || "イベント"}限定グッズ販売`;
      eventTitle.value = title;
      console.log("Event title:", title);
    } else {
      eventTitle.value = "イベント情報が見つかりません";
      console.log("Event document not found");
    }
  } catch (error) {
    console.error("Error fetching event title:", error);
    eventTitle.value = "イベント情報の取得に失敗しました";
  }
};

// コンポーネントマウント時に実行
onMounted(() => {
  fetchEventTitle();
  fetchOrderIds();
});

// 商品の選択機能は不要（数量に基づいて表示のみ）

// 戻るボタン
const goBack = () => {
  router.push("/mycollection");
};
</script>

<template>
  <v-app>
    <!-- メインコンテンツ -->
    <div class="main-container">
      <div class="title-section">
        <h2 class="page-title">マイコレクション</h2>
        <p class="page-description">{{ eventTitle }}</p>
      </div>

      <!-- ローディング表示 -->
      <div v-if="isLoading" class="loading-container">
        <div class="loading-spinner"></div>
        <p class="loading-text">{{ loadingMessage }}</p>
      </div>

      <!-- 商品グリッド -->
      <div v-else class="products-grid">
        <div
          v-for="product in products"
          :key="product.id"
          class="product-card"
          :class="{ 'has-quantity': product.quantity > 0 }"
        >
          <div class="product-image-container">
            <img :src="product.product_image" :alt="product.product_name" class="product-image" />
            <div class="quantity-badge" :class="{ 'has-quantity': product.quantity > 0 }">
              {{ product.quantity }}
            </div>
          </div>
          <div class="product-name">
            {{ product.product_name }}
          </div>
        </div>
      </div>

      <!-- 戻るボタン -->
      <div class="return-container">
        <button class="return-btn" @click="goBack">戻る</button>
      </div>
    </div>
  </v-app>
</template>

<style scoped>
/* メインコンテンツ */
.main-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  background-color: white;
  min-height: 60vh;
}

.title-section {
  text-align: center;
  margin-bottom: 40px;
}

.page-title {
  font-size: 24px;
  font-weight: bold;
  color: #000;
  margin: 0 0 10px 0;
}

.page-description {
  font-size: 16px;
  font-weight: bold;
  color: #000;
  margin-top: 20px;
}

.debug-info {
  font-size: 12px;
  color: #666;
  margin: 5px 0;
}

/* ローディング表示 */
.loading-container {
  text-align: center;
  padding: 60px 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #ff69b4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 16px;
  color: #666;
  margin: 0;
}

/* 商品グリッド */
.products-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 40px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.product-card {
  background-color: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
  position: relative;
}

.product-card.has-quantity {
  border-color: #ff69b4;
}

.product-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.product-image-container {
  position: relative;
  width: 100%;
  height: 180px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
}

.product-image {
  width: 150px !important;
  height: 150px !important;
  object-fit: contain;
  max-width: none;
  max-height: none;
}

.quantity-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: #e0e0e0;
  color: #000;
  border-radius: 12px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: bold;
  min-width: 20px;
  text-align: center;
}

.quantity-badge.has-quantity {
  background-color: #ff69b4;
  color: white;
}

.product-name {
  padding: 12px 8px;
  font-size: 12px;
  font-weight: bold;
  color: #000;
  text-align: center;
  background-color: white;
  line-height: 1.2;
}

/* 戻るボタン */
.return-container {
  text-align: center;
  margin-top: 40px;
}

.return-btn {
  padding: 15px 20px !important;
  font-size: 0.9rem !important;
  border-radius: 100px !important;
  border: none !important;
  margin: 0 5px !important;
  background: rgb(226, 226, 226) !important;
  color: rgb(142, 144, 147) !important;
  box-shadow: none !important;
  cursor: pointer;
}

.return-btn:active,
.return-btn:hover {
  background: rgb(190, 190, 190) !important;
  color: rgb(255, 255, 255) !important;
}

/* レスポンシブデザイン */
@media (max-width: 768px) {
  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }

  .product-image-container {
    height: 150px;
  }

  .product-image {
    width: 120px !important;
    height: 120px !important;
  }
}

@media (max-width: 480px) {
  .products-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
}
</style>
